import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHDate from "_shared/dates/date";
import { AnswerFeedbackCategory, AnswerFeedbackReason, categoryOfAnswerFeedbackReason } from "ai-assistant/feedback/answer-feedback-reason";
import AnswerFeedbackSchema, { AnswerFeedbackJSON, AnswerFeedbackParams } from "ai-assistant/feedback/answer-feedback-schema";

/** The longest note the server keeps beside a reason. */
export const MaximumAnswerFeedbackCommentLength = 1000;

/**
 * A user's report that an answer they were given was wrong, and why (#5204).
 *
 * It names the answer by the trace id its reply carried and copies nothing from it: the question,
 * the answer and its sources are already on the server's record of the turn.
 */
export default class AnswerFeedback {
  public static create(values: AnswerFeedbackParams): AnswerFeedback {
    return new AnswerFeedback(AnswerFeedbackSchema.validate(values));
  }

  public static fromJSON(json: AnswerFeedbackJSON): AnswerFeedback {
    if (isNullOrUndefined(json)) return json;
    return new AnswerFeedback(AnswerFeedbackSchema.deserialize(json));
  }

  public readonly reason: AnswerFeedbackReason;
  public readonly comment: string | undefined;
  public readonly reportedAt: AHDate;

  public get category(): AnswerFeedbackCategory {
    return categoryOfAnswerFeedbackReason(this.reason);
  }

  public constructor(params: AnswerFeedbackParams) {
    this.reason = params.reason;
    this.comment = params.comment;
    this.reportedAt = params.reportedAt;
  }

  public toJSON(): AnswerFeedbackJSON {
    return {
      reason: this.reason,
      comment: this.comment,
      reportedAt: this.reportedAt.toISOString(),
    };
  }

  public isEqualTo(other: AnswerFeedback): boolean {
    return this.reason === other.reason
      && this.comment === other.comment
      && this.reportedAt.isEqualTo(other.reportedAt);
  }

  public toString(): string {
    return `AnswerFeedback(${AnswerFeedbackReason[this.reason]})`;
  }
}
